import { call } from 'redux-saga/effects';
import moment from 'moment';
import { generate, templateLocationExists } from '../../../core/js/utils/componentUtils';
import { getCarousel } from '../../../core/js/components/carouselComponent';

import { error } from '../../../core/js/utils/logger';

import {
    profileAvailabilityError,
    loyaltySignupFailure,
    profileAvailabilitySuccess,
    saveLoyaltyValidation,
    saveLoyaltyFormData,
    setLoyaltyBirthdayYears,
    setLoyaltyBirthdayOrder
} from '../actions/loyaltyActions';

import {
    loyaltySignupSaga,
    profileAvailabilitySaga,
    loyaltyInterstitialDeclinedSaga
} from "../sagas/sagas";

import template from 'loyalty-signup.j2';

import { requestProfile } from '../../../core/js/actions/initialActions.js';

import {
    checkForm,
    shouldActivateParsley
} from '../../../core/js/utils/errorHandling';

import {
    scrollToElement
} from '../../../core/js/utils/scrollUtils';

import { createGAEvent } from '@core/js/utils/analyticsUtils';
import { handleUtagEvent } from '@core/js/actions/initialActions.js';

import Cookies from '../../../core/js/services/cookies';

import {
    COOKIE_NAME_URBN_IS_LOYALTY,
    COOKIE_PERSONALIZATION_CONTEXT,
    DEFAULT_LOYALTY_BIRTHYEAR,
} from '../../../core/js/constants';

import { updateButtonTextSaga } from '../../../core/js/sagas/animateButtonSagas';

import { setDefaultCountry } from '../../../core/js/utils/countryUtils';

import { getOptionsForUpdateButton } from '../../../core/js/utils/requestUtils';

import { mDom, updateElChildrenValueOnRender } from '../../../core/js/utils/renderUtils';

import { getYears } from '../../../core/js/utils/dateUtils';

import {
    requestLoyaltySubscription
} from '../../../core/js/actions/userActions.js';

import { init as ugcComponentInit } from '../../../core/js/components/ugcComponent';

import { launchAsyncSignInModal } from '@core/js/utils/asyncLaunchers';

const DEFAULT_DAYS_IN_MONTH = 31;
/**
 * Bootstrap the component onto the page
 * @param {Object} store Redux store
 * @param {Object} overrides Component-specific overrides
 * @returns {undefined}
 */
export function init(store, overrides = {}) {
    const config = {
        children: [
            [ugcComponentInit, {
                feature: 'core.loyaltyForm',
                loader: {
                    onWindow: true
                },
                isComponentValid: () => true
            }]
        ],
        name: 'loyaltyComponent',
        template: template,
        watch: [
            'core.profile',
            'core.loyaltyForm',
            'core.currentGroupType',
            'core.validations',
            'core.loyaltyForm.signupSuccess'
        ],
        templateLocation: '.dom-loyalty-page',
        attachEvents: attachEvents,
        isComponentValid: templateLocationExists,
        renderType: mDom,
        firstRender: firstRender,
        afterRender: afterRender,
        target: '.js-carousel',
        onStateChange: onStateChange,
        renderOptions: {
            onBeforeElChildrenUpdated: (fromEl, toEl) => {
                if ($(fromEl).hasClass('dom-ugc') || $(fromEl).hasClass('js-contentful-carousel')) {
                    return false;
                }

                toEl = updateElChildrenValueOnRender(fromEl, toEl);

                return true;
            }
        }
    };
    return generate(config, store, overrides);
}

export function onStateChange(store, watchValue, oldVal, newVal) {
    const state = store.getState();
    const profileId = _.get(state.core, 'profile.profileId', '');
    const hasAddresses = _.get(state.core, 'profile.hasAddresses', false);
    const showShortForm = profileId !== '' && hasAddresses;
    const showLongForm = profileId !== '' && !hasAddresses;

    const dictionary = {
        showShortForm: showShortForm,
        showLongForm: showLongForm,
    };

    this.render(store, this, dictionary);
}

/**
 * After render handler; currently used to set country default
 * @param {Object} store Redux store
 */
function afterRender(store) {
    const countrySelect = $(this.scopedSelector('.js-country-select'));
    setDefaultCountry(store, countrySelect);
}

/**
 * Attach component events to DOM
 * @param {Object} store Redux store
 */
export function attachEvents(store) {
    $(document).one(this.scopedEvent('touchend click'), '.js-loyalty-form', (e) => {
        attachParsley(this, store);
    });

    $(document).on(this.scopedEvent('blur'), '.js-loyalty-email-address', (e) => {
        const username = $(e.target).val();

        store.runSaga(profileAvailabilitySaga, username).done.then(() => {
            store.dispatch(profileAvailabilitySuccess());
        }).catch(err => {
            // Let parsley handle validation errors, only dispatch non-validation errors
            if (err.entity && err.entity.code !== 'VALIDATION_ERROR') {
                store.dispatch(profileAvailabilityError(err));
                $('#loyalty-email-address').val('');
            }
        });
    });

    $(document).on(this.scopedEvent('submit'), '.js-loyalty-form', (e) => {
        e.preventDefault();

        const payload = getLoyaltyFormData();
        const formType = e.target.dataset.formType;
        const gaEvent = createGAEvent(
            'PAGE_TYPE',
            'loyalty signup',
            'signup complete'
        );

        const updateOptions = getOptionsForUpdateButton(store);

        const submitButton = $(this.scopedSelector('.js-loyalty-submit-button'));
        submitButton.attr('disabled', true);
        submitButton.addClass('is-disabled');

        // save form values so we can repopulate fields on server error
        store.dispatch(saveLoyaltyFormData(payload));
        store.runSaga(
            updateButtonTextSaga,
            call(loyaltySignupSaga, payload, formType),
            `.js-generic-email-submit`,
            2000,
            updateOptions
        )
        .done.then(() => {
            updateLoyaltyCookie();
            scrollToElement(this.templateLocation);
            getCarousel(this.target);
            store.dispatch(handleUtagEvent(gaEvent, 'link'));
        })
        .catch(err => {
            // request profile on error to update loyalty form state
            store.dispatch(requestProfile());
            store.dispatch(loyaltySignupFailure(err));
            scrollToElement(this.templateLocation);
        });
    });

    $(document).on(this.scopedEvent('touchend click'), '.js-loyalty-interstitial-declined', (e) => {
        e.preventDefault();

        store.runSaga(loyaltyInterstitialDeclinedSaga).done.then(() => {
            this.closeAction(store);
        }).catch(err => {
            error(err);
            this.closeAction(store);
        });
    });

    $(document).on(this.scopedEvent('touchend click'), '.js-join-rewards-link', (e) => {
        e.preventDefault();
        scrollToElement(this.templateLocation, 50);
    });

    // this is duped from headerComponent.js so PWA can get the login flow
    // any changes made there should be reflected here
    $(document).on(this.scopedEvent('touchend click'), '.js-loyalty-signin', (e) => {
        e.preventDefault();
        launchAsyncSignInModal(e, this.name);

        // TODO move logic in a template so that a render
        // won't overwrite this change
        $('.js-site-wrap').attr('aria-hidden', 'true');
    });
}

/**
 * Get number of days in month based on month and year
 * @param {String} month MM representation of month
 * @param {String} year YYYY representation of year
 * @return {Number} days in month
 */
function getDaysInMonth(month, year) {
    if (month && year) {
        return moment(`${year}-${month}`, 'YYYY-MM').daysInMonth();
    }

    return DEFAULT_DAYS_IN_MONTH;
}

/**
 * Format loyalty form values into an object
 * @return {object} loyalty form data
 */
function getLoyaltyFormData() {
    const birthMonth = $('#loyalty-birthday-month').val();
    // NOTE Anthro business logic defaults Perks enrollment birthdate to 1919
    const birthYear = $('#loyalty-birthday-year').val() || DEFAULT_LOYALTY_BIRTHYEAR;
    const daysInMonth = getDaysInMonth(birthMonth, birthYear);

    const loyaltyFormData = {
        loginData: {
            emailAddress: $('#loyalty-email-address').val(),
            password: $('#loyalty-create-password').val(),
            confirmPassword: $('#loyalty-confirm-password').val(),
            gender: $('#loyalty-gender').val(),
            emailSubscription: $('#loyalty-create-email-subscription').prop('checked'),
            source: $('#loyalty-email-subscription-source').val()
        },
        addressData: {
            firstName: $('#loyalty-first-name').val(),
            lastName: $('#loyalty-last-name').val(),
            country: $('#loyalty-country').val(),
            poBox: $('#loyalty-poBox').prop('checked'),
            address1: $('#loyalty-address-1').val(),
            address2: $('#loyalty-address-2').val(),
            address3: $('#loyalty-address-3').val(),
            city: $('#loyalty-city').val(),
            state: $('#loyalty-state').val(),
            zip: $('#loyalty-zip').val(),
            phone: $('#loyalty-phone').val() ? $('#loyalty-phone').val().replace(/\D/g, '') : ''
        },
        birthdayData: {
            birthMonth: birthMonth,
            birthDay: $('#loyalty-birthday-day').val(),
            birthYear: birthYear,
            daysInMonth: daysInMonth
        }
    };

    return loyaltyFormData;
}

/**
 * Bind parsley to the form
 * @param {object} component This component
 * @param {object} store The Redux store
 */
function attachParsley(component, store) {

    if (!shouldActivateParsley($(component.scopedSelector('.js-loyalty-form')))) {
        return;
    }

    function activateValidation() {
        const [values, messages] = checkForm(component.scopedSelector('.js-loyalty-form'));
        const payload = getLoyaltyFormData();

        store.dispatch(saveLoyaltyValidation(values, messages));
        store.dispatch(saveLoyaltyFormData(payload));
    }

    function activateFieldValidation() {
        $(component.scopedSelector('.js-loyalty-form')).parsley()
        .on('field:validated', (e) => {
            activateValidation();
        });
    }

    activateFieldValidation();

    $(component.scopedSelector('.js-loyalty-form')).parsley()
    .on('form:validate', (e) => {
        $(component.scopedSelector('.js-loyalty-form')).parsley()
        .off('field:validated');
    });

    $(component.scopedSelector('.js-loyalty-form')).parsley()
    .on('form:validated', (e) => {
        activateValidation();
        activateFieldValidation();
    });
}

/**
 * Set loyalty cookie to true
 * @return {undefined}    Returns nothing
 */
function updateLoyaltyCookie() {
    Cookies.setCookie(COOKIE_NAME_URBN_IS_LOYALTY, true);
    Cookies.deleteCookie(COOKIE_PERSONALIZATION_CONTEXT);
}

function firstRender(store) {
    handleLoyaltyBirthdayCapture(store);
    store.dispatch(requestLoyaltySubscription());
}

export function handleLoyaltyBirthdayCapture(store) {
    const showBirthdayCapture = _.get(
        store.getState(),
        'core.featureConfigs.loyalty-sign-up.showBirthdayCapture',
        false
    );
    const birthdateCaptureFormat = _.get(
        store.getState(),
        'core.featureConfigs.loyalty-sign-up.birthdateCaptureFormat',
        ''
    );
    const birthdateCaptureFormatArray = birthdateCaptureFormat.split('-');
    const birthdateFormatContainsYear = birthdateCaptureFormat.includes('YYYY');

    if (showBirthdayCapture && birthdateCaptureFormatArray.length > 0) {
        store.dispatch(setLoyaltyBirthdayOrder(birthdateCaptureFormatArray));

        if (birthdateFormatContainsYear) {
            const { core } = store.getState();
            const siteConfig = _.find(_.get(core, 'siteConfig.sites', []), (s) => s.siteId === core.siteId);
            const ageRequirement = siteConfig.defaultMinimumRegistrationAge || 0;
            const currentYear = new Date().getFullYear();
            const startYear = currentYear - 150;
            const endYear = currentYear - ageRequirement;

            store.dispatch(setLoyaltyBirthdayYears(getYears(startYear, endYear)));
        }
    }
}
